import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type { EntityOptionResponse } from '@/types/entityOption';
import type {
  EmployeeCodePreviewResponse,
  EmployeePasswordActionRequest,
  EmployeePasswordActionResponse,
  EmployeeRequest,
  EmployeeResponse,
  EmployeeStatusChangeRequest,
  EmployeeStatusHistoryResponse,
} from '@/types/employee';

export const employeesApi = {
  list: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<EmployeeResponse>>>('/v1/employees', { params })),

  listOptions: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<EntityOptionResponse>>>('/v1/employees/options', { params })),

  get: (id: number) => unwrap(api.get<ApiResponse<EmployeeResponse>>(`/v1/employees/${id}`)),

  nextCodePreview: () =>
    unwrap(api.get<ApiResponse<EmployeeCodePreviewResponse>>('/v1/employees/next-code')),

  create: (body: EmployeeRequest) =>
    unwrap(api.post<ApiResponse<EmployeeResponse>>('/v1/employees', body)),

  update: (id: number, body: EmployeeRequest) =>
    unwrap(api.put<ApiResponse<EmployeeResponse>>(`/v1/employees/${id}`, body)),

  delete: (id: number) => unwrap(api.delete<ApiResponse<null>>(`/v1/employees/${id}`)),

  activate: (id: number, active: boolean) =>
    unwrap(
      api.patch<ApiResponse<EmployeeResponse>>(`/v1/employees/${id}/activate`, null, {
        params: { active },
      }),
    ),

  changeStatus: (id: number, body: EmployeeStatusChangeRequest) =>
    unwrap(api.patch<ApiResponse<EmployeeResponse>>(`/v1/employees/${id}/status`, body)),

  statusHistory: (id: number) =>
    unwrap(api.get<ApiResponse<EmployeeStatusHistoryResponse[]>>(`/v1/employees/${id}/status-history`)),

  passwordManagementAccess: () =>
    unwrap(api.get<ApiResponse<boolean>>('/v1/employees/password-management-access')),

  generatePassword: (id: number, body: EmployeePasswordActionRequest = {}) =>
    unwrap(
      api.post<ApiResponse<EmployeePasswordActionResponse>>(
        `/v1/employees/${id}/password/generate`,
        body,
      ),
    ),

  resetPassword: (id: number, body: EmployeePasswordActionRequest = {}) =>
    unwrap(
      api.post<ApiResponse<EmployeePasswordActionResponse>>(
        `/v1/employees/${id}/password/reset`,
        body,
      ),
    ),

  sendCredentials: (id: number, body: EmployeePasswordActionRequest = {}) =>
    unwrap(
      api.post<ApiResponse<EmployeePasswordActionResponse>>(
        `/v1/employees/${id}/password/send-credentials`,
        body,
      ),
    ),

  forcePasswordChange: (id: number, force: boolean) =>
    unwrap(
      api.patch<ApiResponse<EmployeePasswordActionResponse>>(
        `/v1/employees/${id}/password/force-change`,
        null,
        { params: { force } },
      ),
    ),
};
